import { z } from "zod";
const emptyToUndefined = (value) => typeof value === "string" && value.trim() === "" ? undefined : value;
export function normalizeEmail(value) {
    return value.trim().toLowerCase();
}
export function normalizePhone(value) {
    if (!value)
        return undefined;
    const trimmed = value.trim();
    const prefix = trimmed.startsWith("+") ? "+" : "";
    const digits = trimmed.replace(/\D/g, "");
    return digits.length >= 7 ? `${prefix}${digits}` : trimmed;
}
/**
 * Why a text is not a name (an email, a link or a phone typed in the name field), or `undefined` when it is fine.
 * Same rules and messages as the tour registration (margarita.pasosdegigante.com, src/lib/nombre.ts).
 */
export function nameProblem(text) {
    if (text.includes("@"))
        return "Escribe tu nombre aquí; el correo va en su campo.";
    if (/https?:|www\.|\.(com|net|org|mx|me)\b/i.test(text))
        return "Escribe tu nombre, sin enlaces ni correos.";
    if (/\d/.test(text))
        return "Escribe tu nombre sin números; el teléfono va en su campo.";
    return undefined;
}
/** E.164 as sent by the quiz (libphonenumber): "+" and 8–15 digits, no leading zero. Same check as the calendar. */
export const e164PhonePattern = /^\+[1-9]\d{7,14}$/;
/**
 * Contact-quality checks applied only when a new submission arrives (API intake and the browser), never when a
 * stored submission is read for delivery: tightening the durable schemas below would strand accepted leads.
 */
export function contactIntakeIssues(data) {
    const issues = [];
    for (const field of ["firstName", "lastName"]) {
        const problem = nameProblem(data[field]);
        if (problem)
            issues.push({ path: `data.${field}`, message: problem });
    }
    if (!e164PhonePattern.test(data.phone))
        issues.push({ path: "data.phone", message: "Ingresa un teléfono válido con código de país." });
    return issues;
}
// Version 2 is retained because submissions are durable and may be delivered
// after the public form changes. Never add fields to this contract.
export const primaryChallengeValues = [
    "qualified_leads",
    "lead_conversion",
    "high_performance_team",
    "operations",
    "strategic_direction"
];
export const marketingMaturityValues = [
    "referrals",
    "inconsistent_paid",
    "multichannel",
    "inbound_system"
];
export const delegationLevelValues = [
    "owner_operated",
    "owner_led_team",
    "operations_delegated",
    "leadership_team"
];
export const cashFlowStabilityValues = [
    "monthly_struggle",
    "one_two_months",
    "three_plus_months",
    "reinvestment_surplus"
];
export const developmentInvestmentValues = [
    "none",
    "limited_low_return",
    "budgeted_positive",
    "regular_high_return"
];
export const timeAllocationValues = [
    "operational_work",
    "supervising_problems",
    "leading_team",
    "strategy_growth_talent"
];
export const dailyDependenceValues = [
    "owner_personally",
    "owner_approval",
    "team_needs_frequently",
    "team_independent_growth"
];
export const operatingKnowledgeValues = [
    "owner_head",
    "people_and_isolated_documents",
    "documented_processes",
    "measured_continuously_improved"
];
export const dailyDecisionsValues = [
    "owner_most",
    "owner_approval_many",
    "area_responsibles",
    "leaders_autonomous"
];
export const problemHandlingValues = [
    "owner_solves",
    "owner_intervenes",
    "responsible_with_support",
    "team_independent"
];
export const priorityManagementValues = [
    "daily_urgencies",
    "too_many_shifting",
    "few_tracked",
    "clear_measured_protected"
];
export const resultAccountabilityValues = [
    "confusion_owner_gaps",
    "responsibles_need_followup",
    "area_responsibles_clear",
    "leaders_autonomous_results"
];
export const resultsTrackingValues = [
    "problem_reactive",
    "irregular",
    "regular_review",
    "kpis_fast_decisions"
];
export const decisionDelegationValues = [
    "hard_to_delegate",
    "tasks_only",
    "within_clear_limits",
    "leaders_decide_develop"
];
export const strategyTimeValues = [
    "almost_none",
    "only_when_urgent",
    "regularly_reserved",
    "agenda_priority"
];
export const growthCapacityValues = [
    "operation_collapses",
    "team_saturates",
    "manageable_with_adjustments",
    "leaders_and_systems_absorb"
];
export const technologyIntegrationValues = [
    "mostly_manual",
    "disconnected_tools",
    "systems_and_automations",
    "integrated_for_scaling"
];
export const teamSizeValues = ["solo", "2_5", "6_15", "16_30", "30_plus"];
export const annualRevenueValues = [
    "no_business",
    "under_100k",
    "100k_500k",
    "500k_1m",
    "1m_5m",
    "over_5m"
];
export const quizV2AnswersSchema = z.strictObject({
    primaryChallenge: z.enum(primaryChallengeValues),
    marketingMaturity: z.enum(marketingMaturityValues),
    delegationLevel: z.enum(delegationLevelValues),
    teamSize: z.enum(teamSizeValues),
    cashFlowStability: z.enum(cashFlowStabilityValues),
    developmentInvestment: z.enum(developmentInvestmentValues),
    annualRevenue: z.enum(annualRevenueValues)
});
export const quizV3AnswersSchema = z.strictObject({
    timeAllocation: z.enum(timeAllocationValues),
    dailyDependence: z.enum(dailyDependenceValues),
    operatingKnowledge: z.enum(operatingKnowledgeValues),
    dailyDecisions: z.enum(dailyDecisionsValues),
    problemHandling: z.enum(problemHandlingValues),
    priorityManagement: z.enum(priorityManagementValues),
    resultAccountability: z.enum(resultAccountabilityValues),
    resultsTracking: z.enum(resultsTrackingValues),
    decisionDelegation: z.enum(decisionDelegationValues),
    strategyTime: z.enum(strategyTimeValues),
    growthCapacity: z.enum(growthCapacityValues),
    technologyIntegration: z.enum(technologyIntegrationValues),
    annualRevenue: z.enum(annualRevenueValues),
    teamSize: z.enum(teamSizeValues)
});
// `quizAnswersSchema` is the current browser contract. Use the versioned
// exports when reading a durable submission.
export const quizAnswersSchema = quizV3AnswersSchema;
const quizContactFields = {
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    email: z.string().trim().max(254).pipe(z.email()).transform(normalizeEmail),
    phone: z.string().trim().min(7).max(40).transform((value) => normalizePhone(value) ?? value),
    consent: z.literal(true)
};
export const quizV2DataSchema = z.strictObject({
    ...quizContactFields,
    answers: quizV2AnswersSchema
});
export const quizV3DataSchema = z.strictObject({
    ...quizContactFields,
    answers: quizV3AnswersSchema
});
export const quizDataSchema = quizV3DataSchema;
// Kept for delivery compatibility with submissions accepted before quiz version 2.
export const contactDataSchema = z.strictObject({
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    email: z.string().trim().max(254).pipe(z.email()).transform(normalizeEmail),
    phone: z.preprocess(emptyToUndefined, z.string().trim().max(40).optional()).transform(normalizePhone),
    company: z.preprocess(emptyToUndefined, z.string().trim().max(160).optional()),
    message: z.string().trim().min(10).max(4000),
    consent: z.literal(true)
});
const submissionClientSchema = z.strictObject({
    sessionId: z.uuid(),
    source: z.string().trim().min(1).max(80).default("website"),
    utmSource: z.string().trim().max(120).nullable(),
    utmMedium: z.string().trim().max(120).nullable(),
    utmCampaign: z.string().trim().max(120).nullable(),
    startedAt: z.iso.datetime({ offset: true }).optional(),
    turnstileToken: z.string().max(2048).optional(),
    website: z.string().max(0).optional(),
    // Analytics only (quiz_leads): never part of the stored submission payload.
    analyticsSessionId: z.uuid().optional(),
    utmContent: z.string().trim().max(120).nullable().optional(),
    utmTerm: z.string().trim().max(120).nullable().optional(),
    clickIdType: z.enum(["gclid", "gbraid", "wbraid", "fbclid"]).nullable().optional(),
    device: z.enum(["mobile", "tablet", "desktop"]).optional(),
    country: z.string().regex(/^[A-Z]{2}$/).nullable().optional()
});
export const submissionRequestSchema = z.discriminatedUnion("formVersion", [
    z.strictObject({
        formVersion: z.literal(2),
        data: quizV2DataSchema,
        client: submissionClientSchema
    }),
    z.strictObject({
        formVersion: z.literal(3),
        data: quizV3DataSchema,
        client: submissionClientSchema
    })
]);
export const submissionAcceptedSchema = z.strictObject({
    submissionId: z.uuid(),
    acceptedAt: z.iso.datetime({ offset: true }),
    status: z.literal("accepted")
});
const webhookSubmissionBase = {
    id: z.uuid(),
    form: z.literal("contact"),
    acceptedAt: z.iso.datetime({ offset: true }),
    source: z.string(),
    utm: z.strictObject({
        source: z.string().nullable(),
        medium: z.string().nullable(),
        campaign: z.string().nullable()
    })
};
const webhookSubmissionSchema = z.discriminatedUnion("formVersion", [
    z.strictObject({
        ...webhookSubmissionBase,
        formVersion: z.literal(1),
        data: contactDataSchema
    }),
    z.strictObject({
        ...webhookSubmissionBase,
        formVersion: z.literal(2),
        data: quizV2DataSchema
    }),
    z.strictObject({
        ...webhookSubmissionBase,
        formVersion: z.literal(3),
        data: quizV3DataSchema
    })
]);
export const webhookEventSchema = z.strictObject({
    eventId: z.uuid(),
    eventType: z.literal("form.submission.created"),
    occurredAt: z.iso.datetime({ offset: true }),
    submission: webhookSubmissionSchema,
    delivery: z.strictObject({
        attempt: z.number().int().positive()
    })
});
export const quizV2AnswerLabels = {
    primaryChallenge: {
        qualified_leads: "Atraer más personas interesadas en lo que vendo.",
        lead_conversion: "Lograr que las personas interesadas se conviertan en clientes.",
        high_performance_team: "Formar un equipo que trabaje sin depender de mí para todo.",
        operations: "Organizar los procesos para que el negocio funcione mejor.",
        strategic_direction: "Salir del día a día para planear el crecimiento del negocio."
    },
    marketingMaturity: {
        referrals: "Dependemos de recomendaciones y no sabemos cuándo llegará el próximo cliente.",
        inconsistent_paid: "Hacemos publicidad o publicamos en redes, pero unos meses llegan clientes y otros muy pocos.",
        multichannel: "Tenemos acciones de marketing que atraen nuevos clientes de forma constante.",
        inbound_system: "Tenemos acciones de marketing que atraen nuevos clientes de forma constante."
    },
    delegationLevel: {
        owner_operated: "Hago casi todo: vendo, atiendo a los clientes y administro.",
        owner_led_team: "Tengo equipo, pero casi todo necesita mi aprobación.",
        operations_delegated: "Mi equipo maneja el día a día, pero me necesita para resolver problemas con frecuencia.",
        leadership_team: "Mi equipo opera y resuelve problemas sin depender de mí; yo me enfoco en la estrategia y el crecimiento."
    },
    teamSize: {
        solo: "Opero solo/a",
        "2_5": "2-5 personas",
        "6_15": "6-15 personas",
        "16_30": "16-30 personas",
        "30_plus": "30+ personas"
    },
    cashFlowStability: {
        monthly_struggle: "Menos de 1 mes.",
        one_two_months: "Entre 1 mes y menos de 3 meses.",
        three_plus_months: "Entre 3 y 6 meses.",
        reinvestment_surplus: "Más de 6 meses"
    },
    developmentInvestment: {
        none: "Aprendemos por nuestra cuenta, usando recursos gratuitos.",
        limited_low_return: "Pagamos por cursos o asesorías cuando surge una necesidad.",
        budgeted_positive: "Seguimos un plan de capacitación con un presupuesto definido.",
        regular_high_return: "Además de capacitarnos, contamos con asesores que nos ayudan a aplicar mejoras y medir los resultados."
    },
    annualRevenue: {
        no_business: "Aún no tengo un negocio.",
        under_100k: "Menos de $100,000 USD.",
        "100k_500k": "Desde $100,000 hasta menos de $500,000 USD.",
        "500k_1m": "Desde $500,000 hasta menos de $1 millón USD.",
        "1m_5m": "Desde $1 millón hasta $5 millones USD.",
        over_5m: "Más de $5 millones USD."
    }
};
export const quizAnswerLabels = {
    timeAllocation: {
        operational_work: "Haciendo trabajo operativo",
        supervising_problems: "Supervisando y resolviendo problemas",
        leading_team: "Dirigiendo al equipo y revisando resultados",
        strategy_growth_talent: "En estrategia, crecimiento y talento"
    },
    dailyDependence: {
        owner_personally: "Hago casi todo personalmente",
        owner_approval: "Tengo equipo, pero casi todo requiere mi aprobación",
        team_needs_frequently: "Mi equipo opera, pero aún me necesita con frecuencia",
        team_independent_growth: "Mi equipo opera sin depender de mí; yo me enfoco en crecer"
    },
    operatingKnowledge: {
        owner_head: "Principalmente en mi cabeza",
        people_and_isolated_documents: "En personas y documentos aislados",
        documented_processes: "En procesos documentados que se siguen",
        measured_continuously_improved: "En procesos medidos y mejorados continuamente"
    },
    dailyDecisions: {
        owner_most: "Casi todas pasan por mí",
        owner_approval_many: "Muchas todavía requieren mi aprobación",
        area_responsibles: "Los responsables deciden dentro de sus áreas",
        leaders_autonomous: "Los líderes deciden con autonomía"
    },
    problemHandling: {
        owner_solves: "Termino resolviéndolo yo",
        owner_intervenes: "Debo intervenir o indicar qué hacer",
        responsible_with_support: "El responsable suele resolverlo con algo de apoyo",
        team_independent: "El equipo lo resuelve sin depender de mí"
    },
    priorityManagement: {
        daily_urgencies: "Según las urgencias del día",
        too_many_shifting: "Tenemos demasiadas y cambiamos de enfoque",
        few_tracked: "Tenemos pocas prioridades y les damos seguimiento",
        clear_measured_protected: "Están claras, medidas y protegidas"
    },
    resultAccountability: {
        confusion_owner_gaps: "Hay confusión y termino cubriendo vacíos",
        responsibles_need_followup: "Hay responsables, pero requieren seguimiento constante",
        area_responsibles_clear: "Cada área tiene responsables y resultados claros",
        leaders_autonomous_results: "Los líderes responden por resultados con autonomía"
    },
    resultsTracking: {
        problem_reactive: "Revisamos cuando aparece un problema",
        irregular: "El seguimiento es irregular",
        regular_review: "Revisamos resultados regularmente",
        kpis_fast_decisions: "Usamos KPIs para detectar problemas y decidir rápido"
    },
    decisionDelegation: {
        hard_to_delegate: "Me cuesta delegarlas",
        tasks_only: "Delego tareas, pero las decisiones regresan a mí",
        within_clear_limits: "Delego decisiones dentro de límites claros",
        leaders_decide_develop: "Los líderes deciden y desarrollan a otros líderes"
    },
    strategyTime: {
        almost_none: "Casi nada",
        only_when_urgent: "Solo cuando disminuyen las urgencias",
        regularly_reserved: "Reservo tiempo regularmente",
        agenda_priority: "Es una prioridad de mi agenda"
    },
    growthCapacity: {
        operation_collapses: "La operación probablemente colapsaría",
        team_saturates: "El equipo y yo nos saturaríamos",
        manageable_with_adjustments: "Podríamos manejarlo con algunos ajustes",
        leaders_and_systems_absorb: "Nuestros líderes y sistemas absorberían gran parte del crecimiento"
    },
    technologyIntegration: {
        mostly_manual: "La mayoría del trabajo sigue siendo manual",
        disconnected_tools: "Tenemos herramientas, pero están desconectadas",
        systems_and_automations: "Usamos sistemas y automatizaciones que mejoran la operación",
        integrated_for_scaling: "Están integrados en procesos clave y ayudan a escalar"
    },
    annualRevenue: {
        no_business: "No tengo negocio",
        under_100k: "Menos de $100,000 USD.",
        "100k_500k": "$100,000 – $500,000 USD",
        "500k_1m": "$500,000 – $1M USD",
        "1m_5m": "$1M – $5M USD",
        over_5m: "Más de $5M USD"
    },
    teamSize: {
        solo: "Solo yo",
        "2_5": "2-5 personas",
        "6_15": "6-15 personas",
        "16_30": "16-30 personas",
        "30_plus": "30+ personas"
    }
};
export const QUIZ_MIN_SCORE = 12;
export const QUIZ_MAX_SCORE = 48;
export const QUIZ_V2_MAX_SCORE = 8;
const scaleQuestionKeys = [
    "timeAllocation",
    "dailyDependence",
    "operatingKnowledge",
    "dailyDecisions",
    "problemHandling",
    "priorityManagement",
    "resultAccountability",
    "resultsTracking",
    "decisionDelegation",
    "strategyTime",
    "growthCapacity",
    "technologyIntegration"
];
const scaleQuestionValues = {
    timeAllocation: timeAllocationValues,
    dailyDependence: dailyDependenceValues,
    operatingKnowledge: operatingKnowledgeValues,
    dailyDecisions: dailyDecisionsValues,
    problemHandling: problemHandlingValues,
    priorityManagement: priorityManagementValues,
    resultAccountability: resultAccountabilityValues,
    resultsTracking: resultsTrackingValues,
    decisionDelegation: decisionDelegationValues,
    strategyTime: strategyTimeValues,
    growthCapacity: growthCapacityValues,
    technologyIntegration: technologyIntegrationValues
};
/**
 * Only prospects who report annual revenue from USD 100,000 are invited to
 * schedule with sales. Shared by the result page offer and the Meta Lead event.
 */
export function isSalesQualified(annualRevenue) {
    return annualRevenue !== "no_business" && annualRevenue !== "under_100k";
}
export function calculateScaleAssessment(answers) {
    const score = scaleQuestionKeys.reduce((total, key) => {
        const optionIndex = scaleQuestionValues[key].indexOf(answers[key]);
        return total + optionIndex + 1;
    }, 0);
    const level = score >= 39 ? "aguila" :
        score >= 30 ? "capitan" :
            score >= 21 ? "pulpo" :
                "zoila_yolo";
    return { score, level };
}
const legacyDelegationPoints = {
    owner_operated: 1,
    owner_led_team: 2,
    operations_delegated: 3,
    leadership_team: 4
};
const legacyTeamPoints = {
    solo: 1,
    "2_5": 2,
    "6_15": 2,
    "16_30": 3,
    "30_plus": 4
};
export function calculateV2ScaleAssessment(answers) {
    const score = legacyDelegationPoints[answers.delegationLevel] + legacyTeamPoints[answers.teamSize];
    const level = score >= 8 ? "aguila" :
        score >= 6 ? "capitan" :
            score >= 4 ? "pulpo" :
                "zoila_yolo";
    return { score, level };
}
/** Quiz events stored for the admin dashboard: the same allowlist and parameters the page sends to GA4. */
export const quizEventNames = [
    "quiz_start", "quiz_resume", "quiz_step_view", "quiz_answer", "quiz_back", "quiz_form_start", "quiz_field_complete",
    "quiz_validation_error", "quiz_submit_attempt", "quiz_submit_error", "quiz_submit_success", "quiz_qualified_lead",
    "quiz_result_view", "quiz_cta_click", "quiz_video_start", "quiz_video_progress", "quiz_video_complete",
    "quiz_page_exit", "quiz_transcript_open"
];
const eventToken = z.string().regex(/^[a-zA-Z0-9_-]{1,80}$/).nullable().optional();
export const quizEventSchema = z.object({
    event: z.enum(quizEventNames),
    at: z.iso.datetime({ offset: true }),
    step: z.number().int().min(0).max(100).nullable().optional(),
    question_id: eventToken,
    answer_id: eventToken,
    previous_answer_id: eventToken,
    elapsed_ms: z.number().int().min(0).max(86_400_000).nullable().optional(),
    field_name: eventToken,
    error_code: eventToken,
    level: eventToken,
    result_offer: eventToken,
    cta_id: eventToken,
    video_percent: z.number().int().min(0).max(100).nullable().optional(),
    transaction_id: z.uuid().nullable().optional()
});
export const quizEventsRequestSchema = z.object({
    sessionId: z.uuid(),
    session: z.object({
        landingPath: z.string().max(300).nullable().optional(),
        utmSource: z.string().trim().max(120).nullable().optional(),
        utmMedium: z.string().trim().max(120).nullable().optional(),
        utmCampaign: z.string().trim().max(120).nullable().optional(),
        utmContent: z.string().trim().max(120).nullable().optional(),
        utmTerm: z.string().trim().max(120).nullable().optional(),
        clickIdType: z.enum(["gclid", "gbraid", "wbraid", "fbclid"]).nullable().optional(),
        device: z.enum(["mobile", "tablet", "desktop"]).nullable().optional(),
        timeZone: z.string().max(64).nullable().optional(),
        quizVersion: z.string().max(60).nullable().optional()
    }).optional(),
    events: z.array(quizEventSchema).min(1).max(50)
});
export const formFields = [
    { name: "firstName", label: "Nombre", type: "text", required: true, maxLength: 80 },
    { name: "lastName", label: "Apellido", type: "text", required: true, maxLength: 80 },
    { name: "phone", label: "Teléfono", type: "tel", required: true, maxLength: 40 },
    { name: "email", label: "Correo electrónico", type: "email", required: true, maxLength: 254 }
];
//# sourceMappingURL=index.js.map